import { format } from "date-fns";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { AttachmentDisplay } from "@/components/support/FileDropZone";
import { Lock } from "lucide-react";

interface ChatBubbleProps {
  message: string;
  senderType: "admin" | "user";
  senderName?: string;
  timestamp: string;
  attachments?: string[];
  isInternal?: boolean;
}

export default function ChatBubble({ message, senderType, senderName, timestamp, attachments, isInternal }: ChatBubbleProps) {
  const isAdmin = senderType === "admin";

  if (isInternal) {
    return (
      <div className="flex gap-2.5">
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className="bg-amber-500/20 text-amber-600 text-xs">
            {senderName?.[0]?.toUpperCase() || "S"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 max-w-[85%] space-y-1">
          <div className="rounded-lg px-3 py-2 text-sm bg-amber-500/10 border border-amber-500/30 text-foreground">
            <div className="flex items-center gap-1.5 mb-1 text-[10px] font-medium text-amber-600 uppercase tracking-wide">
              <Lock className="h-3 w-3" /> Internal note
              {senderName && <span className="text-muted-foreground normal-case font-normal">· {senderName}</span>}
            </div>
            <p className="whitespace-pre-wrap">{message}</p>
            {attachments && attachments.length > 0 && <AttachmentDisplay attachments={attachments} />}
          </div>
          <p className="text-[10px] text-muted-foreground">{format(new Date(timestamp), "MMM d, h:mm a")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-2.5 ${isAdmin ? "flex-row-reverse" : ""}`}>
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className={isAdmin ? "bg-primary/20 text-primary text-xs" : "bg-muted text-muted-foreground text-xs"}>
          {isAdmin ? (senderName?.[0]?.toUpperCase() || "A") : (senderName?.[0]?.toUpperCase() || "U")}
        </AvatarFallback>
      </Avatar>
      <div className={`max-w-[70%] space-y-1 ${isAdmin ? "items-end" : ""}`}>
        {senderName && (
          <p className={`text-[10px] text-muted-foreground ${isAdmin ? "text-right" : ""}`}>{senderName}</p>
        )}
        <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${isAdmin ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted rounded-bl-md"}`}>
          {message}
        </div>
        {attachments && attachments.length > 0 && <AttachmentDisplay attachments={attachments} />}
        <p className={`text-[10px] text-muted-foreground ${isAdmin ? "text-right" : ""}`}>
          {format(new Date(timestamp), "MMM d, h:mm a")}
        </p>
      </div>
    </div>
  );
}
